import { useState } from 'react';

import {
  Badge,
  Button,
  copyTextToClipboard,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@@/ui/primitives';
import {
  TrashIcon,
  FileCodeIcon,
  InfoIcon,
  CopyIcon,
  MoreVerticalIcon,
  CopySlashIcon,
  ClipboardCopyIcon
} from 'lucide-react';
import clsx from 'clsx';
import { useListItems, useDeleteItem } from '../../../../hooks/use-items';
import { ItemRecord } from '../../../../api/api-items';

import { Spinner } from '@@/ui/components';
import { ItemToolbar } from './item-toolbar';
import { EntityRecord } from '../../../../api/api-entities';
import { DefaultValuesComponent } from '../../../../components/defaults';
import { useUpdateEntity } from '../../../../hooks/use-entities';
import { Field, UpdateEntityPayload, Value } from '../../../../schemas/entity';
import { toast } from 'sonner';
import { FieldViewer } from '../../../../components/shared/field-viewers';
import { ItemEditorSheet } from '../../../../components/item-editor/item-create-sheet';
import { cn } from '@@/lib/utils';

function ItemRow({
  entity,
  item,
  columns,
  onNavigate,
  onDuplicate,
  onDelete
}: {
  entity: EntityRecord;
  item: ItemRecord;
  columns: Array<{ fieldName: string; field: Field }>;
  onNavigate: (item: ItemRecord) => void;
  onDuplicate: (item: ItemRecord) => void;
  onDelete: (item: ItemRecord) => void;
}) {
  function handleCopyPath(event: React.MouseEvent, text: string) {
    // Without calling stopPropagation(), the onClick of the TableRow will also
    // be triggered, causing the sheet to be opened.
    event.stopPropagation();
    copyTextToClipboard(text);
  }

  function handleDuplicate(event: React.MouseEvent) {
    event.stopPropagation();
    onDuplicate(item);
  }

  function handleDelete(event: React.MouseEvent) {
    event.stopPropagation();
    onDelete(item);
  }

  return (
    <TableRow
      className={clsx('hover:bg-neutral-100 cursor-pointer transition-none border-y!')}
      onClick={() => onNavigate(item)}
    >
      {columns.length > 0 ? (
        columns.map(({ fieldName, field }) => (
          <TableCell
            key={fieldName}
            className={cn(field.primary && 'font-medium', 'px-4 py-3!', 'wrap-break-word flex-wrap truncate max-w-md')}
          >
            {<FieldViewer value={item.attributes[fieldName] as Value} field={field} showFullContent={false} />}
          </TableCell>
        ))
      ) : (
        <TableCell>
          <pre className='text-wrap text-xs font-mono text-neutral-600'>{JSON.stringify(item.attributes, null, 2)}</pre>
        </TableCell>
      )}
      <TableCell className='w-6 py-0!'>
        <div className='flex items-center justify-end'>
          {/* <Button
            variant='ghost'
            size='sm'
            onClick={handleDuplicate}
            title='Duplicate'
          >
            <CopyIcon className='size-4' />
          </Button> */}
          <DropdownMenu modal={true}>
            <DropdownMenuTrigger asChild>
              <Button variant='ghost' size='icon' className='text-sm! font-medium! rounded-full size-8'>
                <MoreVerticalIcon className='size-4' />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align='end' className='font-sans! font-[450]!'>
              <DropdownMenuItem onClick={(event) => handleCopyPath(event, item.itemId)}>
                <ClipboardCopyIcon />
                Copy ID
              </DropdownMenuItem>
              <DropdownMenuItem onClick={(event) => handleCopyPath(event, `${entity.path}${item.primaryKey}`)}>
                <CopySlashIcon />
                Copy Path
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleDuplicate}>
                <CopyIcon />
                Duplicate
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={handleDelete}
                className='text-red-600! fill-red-600! stroke-red-600! hover:text-red-800'
              >
                <TrashIcon className='text-current' />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </TableCell>
    </TableRow>
  );
}

export function ItemBrowser({
  entity,
  entityId,
  onNavigate
}: {
  entity: EntityRecord;
  entityId: string;
  onNavigate: (item: ItemRecord) => void;
}) {
  const { data: items, isLoading } = useListItems(entityId);
  const { mutateAsync: deleteItem } = useDeleteItem();
  const { mutateAsync: updateEntity } = useUpdateEntity();
  const [isDuplicating, setDuplicating] = useState<ItemRecord | null>(null);

  const handleDelete = (item: ItemRecord) => {
    if (!confirm(`Are you sure you want to delete this item?`)) {
      return;
    }

    const promise = deleteItem({
      entityId: item.entityId,
      itemId: item.itemId
    });

    toast.promise(promise, {
      loading: 'Deleting item...',
      success: 'Item deleted successfully',
      error: (err) => err.message
    });
  };

  const handleSaveDefaults = (defaults: UpdateEntityPayload['defaults']) => {
    const promise = updateEntity({
      entityId: entity.entityId,
      defaults: defaults
    });
    toast.promise(promise, {
      loading: 'Saving default values...',
      success: 'Default values saved successfully',
      error: (err) => err.message
    });
  };

  // Get schema fields and sort by order property
  const getColumns = () => {
    if (!entity?.schema?.fields) return [];

    const fields = Object.entries(entity.schema.fields)
      .map(([fieldName, field]) => ({
        fieldName: fieldName,
        field: field
      }))
      .filter(({ field }) => field.presentable);

    return fields.sort((a, b) => {
      const orderA = a.field.order ?? 0;
      const orderB = b.field.order ?? 0;
      const orderDiff = orderA - orderB;
      if (orderDiff !== 0) return orderDiff;
      return a.fieldName.localeCompare(b.fieldName);
    });
  };

  const columns = getColumns();

  return (
    <div className='space-y-2'>
      <DefaultValuesComponent entity={entity} onSave={handleSaveDefaults} />
      {Object.keys(entity.schema.fields ?? {}).length === 0 && (
        <div className='p-4 text-red-900 bg-red-50 rounded-md items-center flex gap-4'>
          <InfoIcon className='size-6' />
          <div className='flex flex-col'>
            <p className='text-sm font-semibold'>You're almost there!</p>
            <p className='text-sm font-medium'>
              Add fields to define this entity's schema, then you can start creating items.
            </p>
          </div>
        </div>
      )}
      <div className={clsx('overflow-hidden border border-neutral-200 rounded-md')}>
        <div className={cn('flex items-center gap-3 py-2 pl-3 pr-2 justify-between bg-neutral-50')}>
          <div className={clsx('flex items-center gap-3')}>
            <FileCodeIcon className='size-4 text-neutral-500' />
            <div className='flex flex-col'>
              <div className='text-md font-semibold text-neutral-950'>
                Items in <span className='text-blue'>{entity.name}</span>
                <Badge variant='outline' className='ml-2! text-xs text-neutral-500 border-neutral-300'>
                  {(items ?? []).length} {(items ?? []).length === 1 ? 'item' : 'items'}
                </Badge>
              </div>
              {/* <div className="text-xs text-neutral-500">
                {entity.description}
              </div> */}
            </div>
          </div>
          <ItemToolbar entity={entity} />
        </div>
        <Table parentClassname='h-[60rem] bg-neutral-50'>
          <TableHeader className='sticky! top-0! bg-neutral-50 shadow-[0_1px_0_0_var(--color-neutral-200)]!'>
            <TableRow>
              {columns.length > 0 ? (
                columns.map(({ fieldName, field }) => (
                  <TableHead key={fieldName} className={'px-4 text-xs! h-9'}>
                    {fieldName}
                    {field.primary && (
                      <Badge variant='outline' className='ml-2 text-xs'>
                        Primary
                      </Badge>
                    )}
                  </TableHead>
                ))
              ) : (
                <TableHead className='text-xs px-4 h-9'>Attributes</TableHead>
              )}
              <TableHead className='text-xs w-6 text-end px-4 h-9'></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody className='bg-white'>
            {!isLoading && items && items.length === 0 && (
              <TableRow className='hover:bg-inherit'>
                <TableCell colSpan={Math.max(columns.length, 1) + 2} className='text-center py-8 text-neutral-500'>
                  There are no items
                </TableCell>
              </TableRow>
            )}
            {isLoading && (
              <TableRow>
                <TableCell colSpan={Math.max(columns.length, 1) + 2} className='text-center py-8 text-neutral-500'>
                  <div className='flex justify-center items-center flex-1'>
                    <Spinner />
                  </div>
                </TableCell>
              </TableRow>
            )}
            {items && items.length > 0 && (
              <>
                {items.map((item) => (
                  <ItemRow
                    key={item.itemId}
                    entity={entity}
                    item={item}
                    columns={columns}
                    onNavigate={onNavigate}
                    onDuplicate={setDuplicating}
                    onDelete={handleDelete}
                  />
                ))}
              </>
            )}
          </TableBody>
        </Table>
      </div>
      {isDuplicating && (
        <ItemEditorSheet
          entity={entity}
          initialValues={isDuplicating}
          isOpen={isDuplicating !== null}
          onOpenChange={(open) => {
            if (!open) {
              setDuplicating(null);
            }
          }}
        />
      )}
    </div>
  );
}
